// FAR-76: the Months 4–18 tab's calculations. Pure, so the rules the spec
// states -- which months, which SKUs count, the colour bands, the totals -- are
// tested without a browser.

// FAR-83 #14: the tab's months, as slice indexes into the plan's months.
export const FAR_FIRST = 3
export const FAR_LAST = 18

// §1: months 4–18 of the plan, i.e. everything after the first three, up to 15.
export function horizonMonths(months) {
  return (months || []).slice(FAR_FIRST, FAR_LAST)
}

// §3 utilization bands (Open Item 1: "confirm"; constants, so easy to change).
export const UTIL_BANDS = [
  { max: 80, key: 'under', label: 'Under 80%', color: '#DCFCE7', text: '#166534' },
  { max: 90, key: 'good', label: '80–90%', color: '#86EFAC', text: '#14532D' },
  { max: 100, key: 'caution', label: '90–100%', color: '#FDE68A', text: '#78350F' },
  { max: Infinity, key: 'over', label: 'Over 100%', color: '#FCA5A5', text: '#7F1D1D' },
]

export function utilBand(pct) {
  if (pct == null || Number.isNaN(pct)) return null
  return UTIL_BANDS.find((b) => pct < b.max) || UTIL_BANDS[UTIL_BANDS.length - 1]
}

// net available hours = working days × hours/day − changeover (never < 0)
export function netHours(line, workingDays) {
  if (!line) return 0
  return Math.max(0, (workingDays || 0) * (line.hoursPerDay || 0) - (line.changeoverHoursPerMonth || 0))
}

// §3: a line's utilization in a month = Σ hours of SKUs whose PRIMARY line is
// that line ÷ net available hours. null when there are no available hours,
// so the card shows "—" instead of an infinite percentage.
export function lineUtilization(rows, lineName, monthKey, capacityLine, workingDays) {
  const avail = netHours(capacityLine, workingDays)
  if (avail <= 0) return null
  const hours = (rows || [])
    .filter((r) => r.line === lineName)
    .reduce((sum, r) => sum + (r[`${monthKey}_hrs`] || 0), 0)
  return (hours / avail) * 100
}

// FAR-83 §3: Overall Capacity -- all lines together. Scheduled hours (every
// SKU's units on its primary line's run rate, which hoursFor already gives)
// over the sum of Lines 1-4's net available hours (working days x hours per
// day, less changeover). null when no line has hours, so "—" shows.
export function overallUtilization(rows, monthKey, capacityLines, workingDays) {
  const avail = (capacityLines || []).reduce((sum, l) => sum + netHours(l, workingDays), 0)
  if (avail <= 0) return null
  const hours = (rows || []).reduce((sum, r) => sum + (r[`${monthKey}_hrs`] || 0), 0)
  return (hours / avail) * 100
}

// §3: % of SKUs *produced that month* (units > 0) whose month-end DOS is below
// Min / above Max. SKUs with no DOS band are excluded (§5), so made-to-order
// items (no band) are too. null when nothing is produced.
export function dosRiskPct(rows, monthKey, which) {
  const produced = (rows || []).filter((r) =>
    (r[`${monthKey}_cases`] || 0) > 0 && hasBand(r) && r[`${monthKey}_dos`] != null && !r[`${monthKey}_noDemand`])
  if (produced.length === 0) return null
  const hit = produced.filter((r) => {
    const dos = r[`${monthKey}_dos`]
    return which === 'below' ? dos < r.dosMin : dos > r.dosMax
  })
  return (hit.length / produced.length) * 100
}

export function hasBand(r) {
  return (r?.dosMin || 0) > 0 && (r?.dosMax || 0) > 0
}

// §4 DOS cell colouring: red below Min, amber above Max, none in band or unbanded.
export function dosStatus(r, dos, monthKey) {
  if (dos == null || !hasBand(r)) return 'none'
  // FAR-84 §4.1: DOS 0 with no demand ahead isn't a shortage.
  if (monthKey && r?.[`${monthKey}_noDemand`]) return 'ok'
  if (dos < r.dosMin) return 'below'
  if (dos > r.dosMax) return 'above'
  return 'ok'
}

// §4 filters: primary line (or all) AND status (all / below / above). A SKU
// "is below Min" if any month in the horizon is.
export function filterRows(rows, months, { line = '', status = 'all' } = {}) {
  return (rows || []).filter((r) => {
    if (line && r.line !== line) return false
    if (status === 'all') return true
    return (months || []).some((m) => dosStatus(r, r[`${m.key}_dos`], m.key) === status)
  })
}

// §4 footer totals: over ALL SKUs (ignoring filters), in CASES. Pallet items
// can't be added to cases without a cases-per-pallet factor we don't have
// (Open Item 3), so they're excluded and counted.
export function footerTotals(rows, months, baseRowsByItem) {
  const cases = (rows || []).filter((r) => (r.baseUom || 'CS') === 'CS')
  const excluded = (rows || []).length - cases.length
  const perMonth = {}
  let cumUnits = 0
  let cumDemand = 0
  for (const m of months || []) {
    let units = 0
    let demand = 0
    let inventory = 0 // FAR-77 footer; 4–18 doesn't show it
    let base = 0
    let dosSum = 0
    let dosN = 0
    let baseKnown = true
    for (const r of cases) {
      units += r[`${m.key}_cases`] || 0
      demand += r[`${m.key}_fcst`] || 0
      inventory += r[`${m.key}_endInv`] || 0 // made to order counts (FAR-83: typed surplus is real stock)
      const dos = r[`${m.key}_dos`]
      // No demand ahead: DOS 0 by rule, not a level to average in.
      if (dos != null && hasBand(r) && !r[`${m.key}_noDemand`]) { dosSum += dos; dosN += 1 }
      const b = baseRowsByItem?.[r.itemNumber]
      if (b) base += b[`${m.key}_cases`] || 0
      else baseKnown = false
    }
    cumUnits += units
    cumDemand += demand
    perMonth[m.key] = {
      units,
      demand,
      inventory,
      avgDos: dosN ? dosSum / dosN : null,
      cumPlanVsDemand: cumUnits - cumDemand,
      cumPlanVsDemandPct: cumDemand ? ((cumUnits - cumDemand) / cumDemand) * 100 : null,
      deltaVsBase: baseRowsByItem && baseKnown ? units - base : null,
      deltaVsBasePct: baseRowsByItem && baseKnown && base ? ((units - base) / base) * 100 : null,
    }
  }
  return { perMonth, excludedPallet: excluded }
}

// ── FAR-76 §4: editing Units ────────────────────────────────────────────────
// A planner's typed Units are one "Cell" modification per SKU (scope
// ItemNumber, monthValues {iso: units}). The engine applies it last, as an
// absolute value.

export const CELL = 'Cell'

// The SKU's Cell modification, if any. Ignored ones count as "no edits".
export function cellModFor(modifications, itemNumber) {
  return (modifications || []).find(
    (m) => m.changeType === CELL && !m.isIgnored && String(m.identifier) === String(itemNumber),
  ) || null
}

// Parse what was typed: "4,800" -> 4800. null when it isn't a number >= 0.
export function parseUnits(text) {
  const s = String(text ?? '').replace(/[,\s]/g, '')
  if (s === '') return null
  const n = Number(s)
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null
}

// Open Item 5 (Tyler, 2026-10-17): save what the planner types; warn when it
// isn't a multiple of the item's lot size. Returns the lot size, or null.
export function lotWarning(units, roundingValue) {
  if (units == null || !roundingValue || roundingValue <= 0) return null
  return units % roundingValue === 0 ? null : roundingValue
}

// The SKU's monthValues after typing `units` into month `iso`. Typing the
// Base Plan's value removes that month's edit. Returns {} when no edits remain
// (the caller deletes the modification).
export function nextCellValues(existing, iso, units, baseUnits) {
  const next = { ...(existing?.monthValues || {}) }
  if (baseUnits != null && Math.round(baseUnits) === units) delete next[iso]
  else next[iso] = units
  return next
}

// ── FAR-76 §5: Apply Modification ───────────────────────────────────────────
// Checked SKUs, a percent (0–200, step 10, default 100) of a basis DOS (Min /
// Target / Max, default Min). Saved as one "DOSTarget" rule; the engine works
// out each month's units as inventory chains.

export const DOS_TARGET = 'DOSTarget'
export const BASES = ['Min', 'Target', 'Max']
export const PCT_STEPS = Array.from({ length: 21 }, (_, i) => i * 10)

// Open Item 7: only checked rows that are visible under the current filters.
export function checkedVisible(rows, months, filters, selected) {
  return filterRows(rows, months, filters)
    .filter((r) => selected?.has(r.itemNumber))
    .map((r) => r.itemNumber)
}

// "100% of Min DOS on 3 SKUs"
export function dosTargetName(pct, basis, count) {
  return `${pct}% of ${basis} DOS on ${count} SKU${count === 1 ? '' : 's'}`
}

// The rule's body. It covers every horizon month the tab shows.
export function dosTargetMod(items, pct, basis, months) {
  const monthValues = {}
  for (const m of months || []) monthValues[m.iso] = pct
  return {
    name: dosTargetName(pct, basis, items.length),
    scope: 'Items', items: [...items], basis,
    changeType: DOS_TARGET, monthValues,
  }
}
